import React from 'react'
import Select, { components } from 'react-select'
import { SelectProps } from './Select.types'

import { useStyletron } from 'styletron-react'
import * as styles from './Styles'
import * as inputStyles from '../Input/Styles'

import Icon from '../Icon'

const ClearIndicator = props => {
  const {
    innerProps: { ref, ...restInnerProps },
  } = props

  const [css] = useStyletron()
  return (
    <div
      {...restInnerProps}
      style={{
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        width: 'var(--size-default)',
        justifyContent: 'center',
      }}
    >
      <Icon name="X" className={css(styles.clearIcon)} />
    </div>
  )
}
const DropdownIndicator = props => {
  const {
    innerProps: { ref, ...restInnerProps },
  } = props

  const [css] = useStyletron()
  return (
    <div
      {...restInnerProps}
      style={{
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        width: 'var(--size-default)',
        justifyContent: 'center',
      }}
    >
      <Icon name="ChevronDown" className={css(styles.dropdownIndicator)} />
    </div>
  )
}

const NoOptionsMessage = props => {
  return (
    <components.NoOptionsMessage {...props}>
      No options yet.
      {props.type === 'autocomplete' && 'Type and press enter to add an option.'}
    </components.NoOptionsMessage>
  )
}

const AutocompleteSelect = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ placeholder = '', isClearable = true, ...props }, ref) => {
    const [css] = useStyletron()

    let containerStyles = css({
      ...inputStyles.container({ width: props.width, flexGrow: props.flexGrow }),
    })
    let labelStyles = css({
      ...inputStyles.label({ label: props.label, disabled: props.disabled }),
    })

    const selectStyles = {
      control: (provided, state) => ({
        ...styles.control(provided, state, props.disabled, props.error, props.type),
      }),
      valueContainer: provided => ({ ...styles.valueContainer(provided) }),
      indicatorsContainer: () => ({ ...styles.indicatorsContainer }),
      menu: (provided, state) => ({
        ...styles.menu(provided, state),
      }),
      option: (provided, state) => ({
        ...styles.option(provided, state),
      }),
      menuPortal: base => ({
        ...base,
        zIndex: 'var(--z4)',
      }),
    }
    let helperStyles = css(inputStyles.helperText({ disabled: props.disabled }))
    let errorStyles = css(inputStyles.errorMsg)
    let errorIconStyles = css(inputStyles.errorMsgIcon)

    var emptyValue = !props.value || props?.value?.value === ''

    return (
      <div className={containerStyles}>
        <label className={labelStyles} id={`${props.name}-label`} htmlFor={props.name}>
          {props.required && '*'}
          {props.label}
        </label>

        <Select
          {...props}
          error={props.error}
          styles={selectStyles}
          components={{
            DropdownIndicator: props.type === 'default' ? DropdownIndicator : () => null,
            IndicatorSeparator: () => null,
            ClearIndicator: emptyValue ? () => null : ClearIndicator,
            NoOptionsMessage,
          }}
          isClearable={isClearable}
          placeholder={placeholder}
          value={emptyValue ? null : { label: props.value.label, value: props.value.value }}
          onBlur={props.onBlur}
          ref={ref}
          aria-labelledby={
            props['aria-labelledby'] ? props['aria-labelledby'] : `${props.name}-label`
          }
          menuPortalTarget={document.body}
          onMenuOpen={props.onMenuOpen}
          className={props.className}
          classNamePrefix={props.classNamePrefix}
        />
        {props.helper && !props.error && <div className={helperStyles}>{props.helper}</div>}
        {props.errorMsg && props.error && (
          <div className={errorStyles}>
            <Icon name="AlertCircle" className={errorIconStyles} /> {props.errorMsg}
          </div>
        )}
      </div>
    )
  }
)

export default AutocompleteSelect
